import { observer } from 'mobx-react';
import { autoinject, inject, Modal, ModalHandle } from 'Mri.Web.Common';
import * as React from 'react';
import { Button } from 'reactstrap';
import { CrashDumps, InfrastructureStore, OverreactionModeStatus, PodScaleDownPlan, PodStatus, SqlHealth } from './InfrastructureStore';

interface ActionDetails {
    readonly title: string;
    readonly headerClass: string;
    readonly confirmLabel: string;
    readonly confirmColor: string;
    readonly summary: React.ReactNode;
    readonly target: React.ReactNode;
    readonly runsOn: 'kubernetes' | 'sql';
    readonly wait: string;
    readonly commandLabel: string;
    readonly commands: readonly string[];
    readonly note?: React.ReactNode;
    /** Set when a guard blocks the action — the confirm button stays disabled and only Cancel works. */
    readonly confirmDisabled?: boolean;
}

// The command / replica-count / SQL details below mirror the server-side operations in
// Web/Mri.Web.Internal/Services/KubernetesAdminService.cs
// (s_ScalableDeployments, s_DefaultReplicaCounts, ClearOrleansMembershipAsync).
// Keep them in sync if that file changes.
const SCALE_DOWN_DETAILS: ActionDetails = {
    title: 'Scale Down All',
    headerClass: 'bg-danger text-white',
    confirmLabel: 'Scale everything down',
    confirmColor: 'danger',
    runsOn: 'kubernetes',
    summary: (
        <>
            This takes <strong>all public SmartVU services offline</strong> — Portal, the device API,
            the Orleans silos and the display-update workers. Only this Internal dashboard stays up.
        </>
    ),
    target: (
        <>
            <div>
                The <strong>Kubernetes API server of the cluster this Internal pod runs in</strong> — i.e. the
                environment you're viewing right now, and only that one. Nothing leaves the cluster and no other
                environment is affected.
            </div>
            <ul className="mb-0 mt-1">
                <li><strong>Endpoint:</strong> the in-cluster API (<code>https://kubernetes.default.svc</code>), namespace <code>default</code>.</li>
                <li><strong>Who's asking:</strong> the <code>internal</code> pod's own ServiceAccount token + RBAC — no kubeconfig and no user credentials involved.</li>
                <li><strong>What it does:</strong> a control-plane <code>PATCH</code> of each Deployment's <code>spec.replicas</code> to 0; the kubelet on each node then terminates that tier's pods to match.</li>
                <li><strong>Left intact:</strong> the Deployments, Services, secrets, PVCs and the SQL / RabbitMQ backends — only the pod count changes, so "Bring Everything Back Up" needs no re-deploy.</li>
            </ul>
        </>
    ),
    wait: 'The scale request returns in ~1–2 seconds. Pods then terminate over their grace period; silohost drains Orleans and can take up to ~3 minutes to stop fully.',
    commandLabel: 'Patches spec.replicas → 0 on each deployment (equivalent to):',
    commands: [
        'kubectl -n default scale deployment <env>-portal --replicas=0',
        'kubectl -n default scale deployment <env>-endpoint --replicas=0',
        'kubectl -n default scale deployment <env>-silohost --replicas=0',
        'kubectl -n default scale deployment <env>-display-update-worker --replicas=0',
    ],
    note: 'The Internal tier (this dashboard) is intentionally excluded so this page stays reachable.',
};

const CLEAR_ORLEANS_DETAILS: ActionDetails = {
    title: 'Clear Orleans Membership',
    headerClass: 'bg-warning text-dark',
    confirmLabel: 'Clear Orleans tables',
    confirmColor: 'warning',
    runsOn: 'sql',
    summary: (
        <>
            Empties the silo ROSTER so silos can re-register, and nothing else.{' '}
            <strong>Only run this after Scale Down All.</strong> If silos are still running they will lose
            track of each other and the cluster will break.
        </>
    ),
    target: (
        <>
            <div>
                This one is <strong>not a Kubernetes action</strong> — it's a direct database connection from
                this Internal pod to SQL Server, bypassing the cluster API entirely.
            </div>
            <ul className="mb-0 mt-1">
                <li><strong>Target:</strong> this environment's <code>ORLEANS</code> database on its SQL Server (e.g. <code>noc-&lt;env&gt;-db01:1433</code>).</li>
                <li><strong>How:</strong> a direct T-SQL connection over TCP 1433, using the credentials in the pod's <code>OrleansConnectionString</code> secret.</li>
                <li><strong>What it changes:</strong> empties <code>OrleansMembershipTable</code> and bumps the row in <code>OrleansMembershipVersionTable</code>. That is all. Grain state, reminders and stream subscriptions are <strong>not</strong> touched (#64).</li>
                <li><strong>Scope:</strong> only this environment's <code>ORLEANS</code> DB (shared by every silo in this cluster); other environments' databases are untouched.</li>
            </ul>
        </>
    ),
    wait: 'Runs synchronously and returns in a few seconds with the rows-affected for each table.',
    commandLabel: 'Executes against the Orleans database:',
    commands: [
        'BEGIN TRANSACTION',
        'DELETE FROM OrleansMembershipTable',
        'UPDATE OrleansMembershipVersionTable SET Version = Version + 1',
        'COMMIT',
    ],
    note: (
        <>
            <strong>This no longer wipes grain storage, reminders or stream subscriptions (#64).</strong> It
            used to delete all five tables, which caused the 2026-07-20 production outage and needed DB-side
            recovery — a recovery button must not destroy the state it is recovering.
            <br />
            The version row is <strong>bumped, not deleted</strong>: Orleans gates every membership write on
            that row, so removing it means no silo can ever register again. Both statements run in one
            transaction.
            <br />
            You may still need to run it more than once — silos write membership rows as they shut down, so
            repeat until the membership table reports 0 rows.
        </>
    ),
};

const SCALE_UP_DETAILS: ActionDetails = {
    title: 'Bring Everything Back Up',
    headerClass: 'bg-success text-white',
    confirmLabel: 'Bring everything back up',
    confirmColor: 'success',
    runsOn: 'kubernetes',
    summary: (
        <>
            Restores all deployments to their default replica counts. Services start up and the silos
            register fresh entries in the Orleans membership table.
        </>
    ),
    target: (
        <>
            <div>
                Same place as Scale Down — the <strong>Kubernetes API server of this cluster</strong> (the
                environment you're viewing), and only that one.
            </div>
            <ul className="mb-0 mt-1">
                <li><strong>Endpoint:</strong> the in-cluster API (<code>https://kubernetes.default.svc</code>), namespace <code>default</code>.</li>
                <li><strong>Who's asking:</strong> the <code>internal</code> pod's ServiceAccount token + RBAC — no kubeconfig, no user credentials.</li>
                <li><strong>What it does:</strong> a control-plane <code>PATCH</code> of each Deployment's <code>spec.replicas</code> back to its default count; the kubelet then schedules and starts fresh pods.</li>
                <li><strong>Images:</strong> each node re-uses the image already pinned by the Deployment (no rebuild) — it only pulls if that tag isn't already cached on the node.</li>
            </ul>
        </>
    ),
    wait: 'The scale request returns in ~1–2 seconds. Portal and the device API are usually Ready in ~30–60s; all six silohost pods typically take ~1–3 minutes to join the Orleans cluster and pass readiness.',
    commandLabel: 'Patches spec.replicas on each deployment (equivalent to):',
    commands: [
        'kubectl -n default scale deployment <env>-portal --replicas=1',
        'kubectl -n default scale deployment <env>-endpoint --replicas=1',
        'kubectl -n default scale deployment <env>-silohost --replicas=6',
        'kubectl -n default scale deployment <env>-display-update-worker --replicas=10',
    ],
};

@autoinject
@observer
export class InfrastructureDashboard extends React.Component {
    public async componentDidMount() {
        await this.infrastructureStore.loadPodsAsync();
        this.infrastructureStore.loadTargetsAsync();
        this.infrastructureStore.loadCrashDumpsAsync();
        this.infrastructureStore.loadOverreactionModeAsync();
        this.infrastructureStore.loadSqlHealthAsync();
        this.pollInterval = window.setInterval(() => {
            // Hold off while a confirmation is open so the pod list can't shift under the operator
            // mid-decision (the action is still re-planned server-side when they confirm).
            if (!this.infrastructureStore.loading
                && !this.infrastructureStore.actionInProgress
                && !this.infrastructureStore.podPlanLoading
                && !this.podScaleDownModal.show) {
                this.infrastructureStore.loadPodsAsync();
            }
            if (!this.infrastructureStore.overreactionModeSaving && !this.overreactionOnModal.show) {
                this.infrastructureStore.loadOverreactionModeAsync();
            }
        }, 10000);
    }

    public componentWillUnmount() {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
        }
    }

    private pollInterval: number | null = null;

    private scaleDownModal = new ModalHandle();
    private clearOrleansModal = new ModalHandle();
    private scaleUpModal = new ModalHandle();
    private podScaleDownModal = new ModalHandle();
    private overreactionOnModal = new ModalHandle();

    public render() {
        const {
            pods, loading, actionInProgress, lastClearOrleansResults,
            podScaleDownPlan, podPlanLoading, lastPodScaleDownResult, lastPodActionError,
        } = this.infrastructureStore;
        const busy = loading || actionInProgress || podPlanLoading;
        const hasRemainingRows = lastClearOrleansResults.some(r => r.rowsAffected > 0);
        const allClear = lastClearOrleansResults.length > 0 && !hasRemainingRows;
        // Only explain a flag in the legend when it's actually present on the page right now.
        const anyReused = pods.some(p => !!p.runningImage);
        const anyMixed = pods.some(p => p.imageDrift);

        return (
            <div className="container-fluid mt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">Kubernetes Cluster</h4>
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={this.handleRefresh}
                        disabled={busy}
                    >
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>

                <div className="card mb-3">
                    <div className="card-header">Pod Status</div>
                    <div className="card-body p-0">
                        <div className="alert alert-secondary small m-2 mb-0">
                            <div className="mb-1">
                                <strong>Digest is the one image actually running on the pod.</strong> It is the
                                content hash (<code>sha256</code>) of the exact bytes executing — it can't be
                                faked or reused, so it is the single source of truth for what is deployed.
                            </div>
                            <div className="mb-1">
                                <strong>Requested Tag</strong> is only the label the deployment asked for. A tag
                                is a movable pointer — the same tag can be re-pushed to a different build, and a
                                node can keep serving an older cached image under it. The tag tells you intent;
                                the digest tells you reality.
                            </div>
                            <div className="mb-1">
                                <strong>Click a pod's Name</strong> to scale that one pod down — it opens a
                                confirmation that spells out which deployment shrinks and by how much.
                            </div>
                            {(anyReused || anyMixed) && (
                                <div className="mb-0">
                                    {anyReused && (
                                        <div className="mb-1">
                                            <span className="badge bg-warning text-dark me-1">also tagged: …</span>
                                            the running image carries a <em>different</em> tag than requested — the
                                            requested tag was reused, so this pod is NOT the build that tag now
                                            names (redeploy a fresh, unique tag and confirm the digest changes).
                                        </div>
                                    )}
                                    {anyMixed && (
                                        <div className="mb-0">
                                            <span className="badge bg-warning text-dark me-1">mixed</span>
                                            an app's pods aren't all on one digest (rollout in progress, or one
                                            pod stuck on old content).
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {lastPodActionError && (
                            <div className="alert alert-danger small m-2 mb-0">
                                <strong>Pod scale-down did not run.</strong> {lastPodActionError}
                            </div>
                        )}
                        {lastPodScaleDownResult && (
                            <div className="alert alert-success small m-2 mb-0">
                                <strong>Scaled down {lastPodScaleDownResult.podName}.</strong>{' '}
                                {lastPodScaleDownResult.deployment} went from{' '}
                                {lastPodScaleDownResult.previousReplicas} to {lastPodScaleDownResult.newReplicas}{' '}
                                replicas. The pod is terminating and will not be replaced — use{' '}
                                <em>Bring Everything Back Up</em> to restore the count.
                            </div>
                        )}
                        <div className="table-responsive">
                            <table className="table table-sm table-striped mb-0">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>App</th>
                                        <th>Node</th>
                                        <th>Requested Tag</th>
                                        <th>Digest (running image)</th>
                                        <th>Status</th>
                                        <th>Ready</th>
                                        <th>Start Time</th>
                                        <th>Restarts</th>
                                        <th>Last Restart</th>
                                        <th>Restart Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pods.length === 0 && !loading && (
                                        <tr>
                                            <td colSpan={11} className="text-center text-muted">
                                                No pods found
                                            </td>
                                        </tr>
                                    )}
                                    {pods.map((pod) => (
                                        <tr key={pod.name} className={pod.imageDrift ? 'table-warning' : undefined}>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-link btn-sm p-0 align-baseline text-start"
                                                    onClick={() => this.handlePodScaleDown(pod.name)}
                                                    disabled={busy}
                                                    title="Scale down this pod"
                                                >
                                                    {pod.name}
                                                </button>
                                            </td>
                                            <td>{pod.app ?? '-'}</td>
                                            <td>{pod.node ?? '-'}</td>
                                            <td><small className="text-muted">{pod.image ?? '-'}</small></td>
                                            <td>{this.renderImageDigest(pod)}</td>
                                            <td>{this.renderStatusBadge(pod)}</td>
                                            <td>{pod.ready ? 'Yes' : 'No'}</td>
                                            <td>
                                                {pod.startTime
                                                    ? new Date(pod.startTime).toLocaleString()
                                                    : '-'}
                                            </td>
                                            <td>{pod.restartCount}</td>
                                            <td>
                                                {pod.restartTime
                                                    ? new Date(pod.restartTime).toLocaleString()
                                                    : '-'}
                                            </td>
                                            <td>{pod.restartReason ?? '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {this.renderOverreactionMode()}
                {this.renderSqlHealth()}

                <div className="card mb-3">
                    <div className="card-header">Actions</div>
                    <div className="card-body">
                        <div className="mb-4">
                            <h6 className="mb-1">How to perform a full cluster restart</h6>
                            <p className="text-muted small mb-1">
                                Follow these steps in order. Scaling down first ensures a clean shutdown.
                                Clearing Orleans removes stale membership entries so silos can rejoin cleanly
                                on startup. Then scale back up to restore normal operation.
                            </p>
                            <p className="text-muted small mb-0">
                                Each button opens a confirmation dialog first — it spells out the exact commands
                                that will run, the machine they run on, and the expected wait, and nothing happens
                                until you confirm.
                            </p>
                        </div>

                        <div className="d-flex flex-column gap-4">
                            <div className="d-flex align-items-start gap-3">
                                <span className="badge bg-danger rounded-circle p-2 mt-1" style={{ minWidth: '28px', fontSize: '0.85rem' }}>1</span>
                                <div className="flex-grow-1">
                                    <h6 className="mb-1">Scale Down All</h6>
                                    <p className="text-muted small mb-2">
                                        Scales all deployments to 0 replicas except Internal. Use this to gracefully
                                        shut down all services before maintenance or to stop runaway resource usage.
                                    </p>
                                    <button
                                        className="btn btn-danger btn-sm"
                                        onClick={this.handleScaleDown}
                                        disabled={busy}
                                    >
                                        {actionInProgress ? 'Working...' : 'Scale Down All'}
                                    </button>
                                </div>
                            </div>

                            <div className="d-flex align-items-start gap-3">
                                <span className="badge bg-warning text-dark rounded-circle p-2 mt-1" style={{ minWidth: '28px', fontSize: '0.85rem' }}>2</span>
                                <div className="flex-grow-1">
                                    <h6 className="mb-1">Clear Orleans Membership</h6>
                                    <p className="text-muted small mb-2">
                                        Removes all entries from the Orleans membership table. Only do this after
                                        scaling down — if silos are still running, they will lose track of each other
                                        and the cluster will break. This is needed because stale entries prevent
                                        silos from rejoining cleanly on the next startup.
                                        <strong> You may need to run this multiple times</strong> — silo pods
                                        write membership entries as they shut down, so keep clearing until the
                                        silohost pods are fully terminated and no new entries appear.
                                    </p>
                                    <button
                                        className="btn btn-warning btn-sm"
                                        onClick={this.handleClearOrleans}
                                        disabled={busy}
                                    >
                                        {actionInProgress ? 'Working...' : 'Clear Orleans Membership'}
                                    </button>

                                    {lastClearOrleansResults.length > 0 && (
                                        <div className="mt-2">
                                            <div className={`alert ${allClear ? 'alert-success' : 'alert-warning'} py-2 px-3 mb-0`}>
                                                {allClear && <strong>All tables are clear.</strong>}
                                                {hasRemainingRows && <strong>Rows were deleted — run again to verify tables are empty.</strong>}
                                                <table className="table table-sm table-borderless mb-0 mt-1" style={{ fontSize: '0.85rem' }}>
                                                    <tbody>
                                                        {lastClearOrleansResults.map(r => (
                                                            <tr key={r.table}>
                                                                <td className="py-0 ps-0">{r.table}</td>
                                                                <td className="py-0 text-end fw-bold" style={{ width: '100px' }}>
                                                                    {r.rowsAffected === 0 ? (
                                                                        <span className="text-success">0 rows</span>
                                                                    ) : (
                                                                        <span className="text-warning">{r.rowsAffected} rows deleted</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="d-flex align-items-start gap-3">
                                <span className="badge bg-success rounded-circle p-2 mt-1" style={{ minWidth: '28px', fontSize: '0.85rem' }}>3</span>
                                <div className="flex-grow-1">
                                    <h6 className="mb-1">Bring Everything Back Up</h6>
                                    <p className="text-muted small mb-2">
                                        Scales all deployments back to their default replica counts. Services will
                                        start up and silos will register fresh entries in the Orleans membership table.
                                    </p>
                                    <button
                                        className="btn btn-success btn-sm"
                                        onClick={this.handleScaleUp}
                                        disabled={busy}
                                    >
                                        {actionInProgress ? 'Working...' : 'Bring Everything Back Up'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {this.renderLiveManifest()}
                {this.renderCrashDumps()}

                {this.renderActionModal(this.scaleDownModal, SCALE_DOWN_DETAILS)}
                {this.renderActionModal(this.clearOrleansModal, CLEAR_ORLEANS_DETAILS)}
                {this.renderActionModal(this.scaleUpModal, SCALE_UP_DETAILS)}
                {podScaleDownPlan &&
                    this.renderActionModal(this.podScaleDownModal, this.podScaleDownDetails(podScaleDownPlan))}
                {this.renderOverreactionOnModal()}
            </div>
        );
    }

    /**
     * Overreaction Mode (#170): an opt-in watchdog that runs the three Actions below by itself when a
     * silohost pod crashes and doesn't recover. Everything shown here comes from the server - including the
     * implications - so the page always describes what the watchdog will actually do.
     */
    private renderOverreactionMode() {
        const { overreactionMode: s, overreactionModeSaving, overreactionModeError } = this.infrastructureStore;

        return (
            <div className={`card mb-3 ${s?.enabled ? 'border-danger' : ''}`}>
                <div className="card-header d-flex justify-content-between align-items-center">
                    <span>
                        Overreaction Mode{' '}
                        {s && <span className={InfrastructureDashboard.overreactionBadgeClass(s.state)}>{InfrastructureDashboard.overreactionStateLabel(s.state)}</span>}
                        {s?.notRecoveringSinceUtc && (
                            <span
                                className="badge bg-danger ms-1"
                                title="A silohost pod has stayed crashed past the won't-recover line. Flagged whether Overreaction Mode is on or off."
                            >
                                Silohost not recovering since {new Date(s.notRecoveringSinceUtc).toLocaleString()}
                            </span>
                        )}
                    </span>
                    <div className="form-check form-switch mb-0">
                        <input
                            id="overreaction-mode-switch"
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={!!s?.enabled}
                            disabled={!s || overreactionModeSaving}
                            onChange={this.handleOverreactionToggle}
                        />
                        <label className="form-check-label small" htmlFor="overreaction-mode-switch">
                            {overreactionModeSaving ? 'Saving...' : s?.enabled ? 'On' : 'Off'}
                        </label>
                    </div>
                </div>
                <div className="card-body">
                    <p className="small mb-2">
                        When on, Internal watches the silohost pods and, if one <strong>crashes and doesn't
                        recover</strong>, runs the full restart from <em>Actions</em> below by itself:{' '}
                        <strong>Scale Down All → Clear Orleans Membership → Bring Everything Back Up</strong>.
                        Off by default. Use it when nobody is around to react - not as a fix.{' '}
                        <strong>On or off, the moment a silohost pod stops recovering is still flagged and
                        timestamped here</strong>; when off, that is all that happens.
                    </p>

                    {overreactionModeError && (
                        <div className="alert alert-danger small py-2">{overreactionModeError}</div>
                    )}

                    {!s ? (
                        <div className="text-muted small">Loading…</div>
                    ) : (
                        <>
                            <div className={`alert small py-2 ${s.state === 'BreakerTripped' ? 'alert-danger' : s.enabled ? 'alert-warning' : 'alert-secondary'}`}>
                                {s.stateDetail}
                            </div>

                            <div className="fw-bold small mb-1">What turning it on implies</div>
                            <ul className="small mb-3">
                                {s.implications.map((line, i) => <li key={i}>{line}</li>)}
                            </ul>

                            <div className="row small g-2 mb-2">
                                <div className="col-md-6">
                                    <div><strong>Recoveries in the last {s.breakerWindowHours} h:</strong> {s.recoveriesInWindow} of {s.maxRecoveriesPerWindow} allowed</div>
                                    <div><strong>Acts after:</strong> a silohost pod stays crashed {s.unrecoveredAfterMinutes} min; then waits {s.cooldownMinutes} min before another</div>
                                    {s.cooldownUntilUtc && <div><strong>Cooling down until:</strong> {new Date(s.cooldownUntilUtc).toLocaleString()}</div>}
                                </div>
                                <div className="col-md-6">
                                    <div><strong>Acting pod:</strong> <code>{s.leaderPod ?? 'unknown'}</code>{s.servedByPod && s.servedByPod !== s.leaderPod && <> (this page is served by <code>{s.servedByPod}</code>, which only watches)</>}</div>
                                    <div><strong>Last check:</strong> {s.lastCheckUtc ? new Date(s.lastCheckUtc).toLocaleTimeString() : 'not yet'}</div>
                                    {s.changedUtc && <div><strong>Last switched:</strong> {new Date(s.changedUtc).toLocaleString()} by {s.changedBy ?? 'unknown'}</div>}
                                </div>
                            </div>

                            {s.lastCheckError && (
                                <div className="alert alert-warning small py-2">
                                    <strong>The last check failed, so nothing was done:</strong> {s.lastCheckError}
                                </div>
                            )}

                            {s.breakerTrippedUtc && (
                                <div className="alert alert-danger small py-2">
                                    <strong>Circuit breaker tripped {new Date(s.breakerTrippedUtc).toLocaleString()}.</strong>{' '}
                                    {s.breakerReason} Turning the switch back on starts the count afresh.
                                </div>
                            )}

                            {s.crashedSilos.length > 0 && (
                                <table className="table table-sm small mb-2">
                                    <thead>
                                        <tr><th>Crashed silohost pod</th><th>Reason</th><th>Crashed since</th><th>Counts toward a recovery</th></tr>
                                    </thead>
                                    <tbody>
                                        {s.crashedSilos.map(c => (
                                            <tr key={c.podName} className={c.unrecovered ? 'table-danger' : undefined}>
                                                <td><code>{c.podName}</code></td>
                                                <td>{c.reason ?? '-'}</td>
                                                <td>{new Date(c.crashedSinceUtc).toLocaleTimeString()}</td>
                                                <td>{c.unrecovered ? 'Yes - has not recovered' : 'Not yet'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            <div className="fw-bold small mb-1">
                                Flagged moments{' '}
                                <span className="fw-normal text-muted">
                                    - every time a silohost pod crossed the won't-recover line, on or off (last 20)
                                </span>
                            </div>
                            {s.detections.length === 0 ? (
                                <div className="text-muted small mb-2">None recorded.</div>
                            ) : (
                                <table className="table table-sm small mb-2">
                                    <thead>
                                        <tr><th>Flagged at</th><th>Crashing since</th><th>Pods</th><th>Mode</th><th>What happened</th></tr>
                                    </thead>
                                    <tbody>
                                        {s.detections.map(d => (
                                            <tr key={`${d.detectedUtc}-${d.pods.join(',')}`} className={d.modeEnabled ? 'table-warning' : undefined}>
                                                <td className="text-nowrap">{new Date(d.detectedUtc).toLocaleString()}</td>
                                                <td className="text-nowrap">{d.crashedSinceUtc ? new Date(d.crashedSinceUtc).toLocaleString() : '-'}</td>
                                                <td>{d.pods.map(p => <div key={p}><code>{p}</code></div>)}</td>
                                                <td>
                                                    <span className={d.modeEnabled ? 'badge bg-danger' : 'badge bg-secondary'}>
                                                        {d.modeEnabled ? 'On' : 'Off'}
                                                    </span>
                                                </td>
                                                <td>{d.outcome}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {s.lastRecovery && (
                                <details className="small">
                                    <summary>
                                        Last recovery: <strong>{s.lastRecovery.outcome ?? 'in progress'}</strong>{' '}
                                        {new Date(s.lastRecovery.startedUtc).toLocaleString()}
                                        {s.lastRecovery.actedBy && <> by <code>{s.lastRecovery.actedBy}</code></>}
                                    </summary>
                                    <div className="mt-1">Triggered by: {s.lastRecovery.triggerPods.join(', ') || '-'}</div>
                                    {s.lastRecovery.error && <div className="text-danger">Error: {s.lastRecovery.error}</div>}
                                    <ol className="mb-0 mt-1">
                                        {s.lastRecovery.steps.map((step, i) => <li key={i}><code>{step}</code></li>)}
                                    </ol>
                                </details>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    }

    private renderOverreactionOnModal() {
        const s = this.infrastructureStore.overreactionMode;
        return (
            <Modal
                size="lg"
                modalHandle={this.overreactionOnModal}
                titleClass="bg-danger text-white"
                title={<span className="fw-bold">Turn on Overreaction Mode</span>}
                footer={
                    <>
                        <Button color="secondary" outline onClick={this.overreactionOnModal.reject}>
                            Cancel
                        </Button>
                        <Button color="danger" onClick={this.overreactionOnModal.resolve}>
                            Turn on Overreaction Mode
                        </Button>
                    </>
                }
            >
                <div className="alert alert-danger mb-3">
                    From now on, <strong>nobody will be asked</strong>: if a silohost pod crashes and doesn't recover,
                    Internal takes <strong>all public SmartVU services offline</strong>, clears Orleans and brings them
                    back up on its own.
                </div>
                <div className="fw-bold mb-1">What this implies</div>
                <ul className="small mb-3">
                    {(s?.implications ?? []).map((line, i) => <li key={i}>{line}</li>)}
                </ul>
                <div className="alert alert-secondary small mb-0">
                    It is a symptom treatment. The prd watchdog it replaces turned a one-node fault into the 2026-07-20
                    rolling outage by restarting everything every minute; this one stops after{' '}
                    {s?.maxRecoveriesPerWindow ?? 2} recoveries in {s?.breakerWindowHours ?? 6} hours and switches itself off.
                </div>
            </Modal>
        );
    }

    private static overreactionStateLabel(state: string): string {
        switch (state) {
            case 'CoolingDown': return 'Cooling down';
            case 'BreakerTripped': return 'Breaker tripped - off';
            default: return state;
        }
    }

    private static overreactionBadgeClass(state: string): string {
        switch (state) {
            case 'Watching': return 'badge bg-success';
            case 'Crashed': return 'badge bg-warning text-dark';
            case 'Recovering': return 'badge bg-danger';
            case 'CoolingDown': return 'badge bg-info text-dark';
            case 'BreakerTripped': return 'badge bg-danger';
            default: return 'badge bg-secondary';
        }
    }

    // Turning it ON needs a confirmation that spells out the implications; turning it OFF never does -
    // switching the watchdog off is always the safe direction.
    private handleOverreactionToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const enable = e.target.checked;
        if (enable && !(await this.overreactionOnModal.confirm())) {
            return;
        }
        await this.infrastructureStore.setOverreactionModeAsync(enable);
    };

    /**
     * The LIVE cluster, rendered in manifest form.
     *
     * Labelled as live throughout and never presented as "the manifest", because it is not the manifest —
     * it is what the API server currently holds, which is the only thing that is certainly true. A rendered
     * kustomize output states what SHOULD be deployed, and the two diverge whenever something was committed
     * but not applied, applied but not committed, or edited by hand.
     *
     * Loaded on demand rather than with the page: it is a much heavier read than the pod list, and most
     * visits to this page do not want it.
     */
    /**
     * SQL Server worker threads and the two waits that explain a Portal timeout.
     *
     * This is here because a database under thread or memory-grant pressure reports NOTHING to the
     * cluster view - every pod stays green and Ready - while the Portal returns "A task was canceled."
     * or a 503 "Overloaded". Those are timeouts, so there is no error anywhere to go and find.
     */
    private renderSqlHealth() {
        const h: SqlHealth | null = this.infrastructureStore.sqlHealth;
        const loading = this.infrastructureStore.sqlHealthLoading;

        if (!h && loading) {
            return (
                <div className="card mb-3">
                    <div className="card-header">SQL Server Threads &amp; Waits</div>
                    <div className="card-body"><span className="text-muted small">Reading counters...</span></div>
                </div>
            );
        }
        if (!h) return null;

        if (!h.available) {
            return (
                <div className="card mb-3">
                    <div className="card-header">SQL Server Threads &amp; Waits</div>
                    <div className="card-body">
                        <div className="alert alert-warning small mb-0">
                            {h.unavailableReason || 'The counters could not be read.'}
                        </div>
                    </div>
                </div>
            );
        }

        // Cumulative totals are meaningless without uptime: 60 wait-hours over a week is routine, the
        // same figure over a day is not. Normalising makes the two comparable across environments.
        const perHour = (hours: number) => (h.uptimeHours > 0 ? hours / h.uptimeHours : 0);
        const threadUse = h.maxWorkerThreads > 0 ? h.currentWorkers / h.maxWorkerThreads : 0;
        const threadClass = threadUse >= 0.9 ? 'text-danger' : threadUse >= 0.7 ? 'text-warning' : '';
        const num = (n: number) => n.toLocaleString();
        const hrs = (n: number) => n.toFixed(1);

        return (
            <div className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <span>SQL Server Threads &amp; Waits</span>
                    <span className="badge bg-secondary">
                        {h.serverName || 'unknown instance'} &middot; {h.cpuCount} CPU &middot; up {hrs(h.uptimeHours)} h
                    </span>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-4 mb-2">
                            <div className="text-muted small">Worker threads in use</div>
                            <div className={`h4 mb-0 ${threadClass}`}>
                                {num(h.currentWorkers)} <span className="text-muted">/ {num(h.maxWorkerThreads)}</span>
                            </div>
                            <div className="small text-muted">
                                {num(h.activeWorkers)} actively running.{' '}
                                {h.configuredMaxWorkerThreads === 0
                                    ? <>Ceiling is <strong>automatic</strong> (512 at 4 CPUs or fewer, else 512 + (CPUs &minus; 4) &times; 16).</>
                                    : <>Ceiling is <strong>set explicitly</strong> to {num(h.configuredMaxWorkerThreads)}.</>}
                            </div>
                        </div>
                        <div className="col-md-4 mb-2">
                            <div className="text-muted small">THREADPOOL waits</div>
                            <div className="h4 mb-0">{num(h.threadPoolWaits)}</div>
                            <div className="small text-muted">
                                {hrs(h.threadPoolWaitHours)} h total &middot; {hrs(perHour(h.threadPoolWaitHours))} h per hour of uptime
                            </div>
                        </div>
                        <div className="col-md-4 mb-2">
                            <div className="text-muted small">RESOURCE_SEMAPHORE waits</div>
                            <div className="h4 mb-0">{num(h.resourceSemaphoreWaits)}</div>
                            <div className="small text-muted">
                                {hrs(h.resourceSemaphoreWaitHours)} h total &middot; {hrs(perHour(h.resourceSemaphoreWaitHours))} h per hour of uptime
                            </div>
                        </div>
                    </div>

                    <div className="alert alert-secondary small mb-0">
                        <div className="mb-1">
                            <strong>Worker threads</strong> are SQL Server&apos;s pool of query-execution slots. Every
                            batch borrows one for as long as it runs, and a parallel query borrows several. The pool
                            is fixed, so it is a hard ceiling on how much can run at once.
                        </div>
                        <div className="mb-1">
                            <strong>THREADPOOL</strong> counts requests that arrived, found no free worker and queued
                            at the door before starting. It is almost always a SYMPTOM of queries holding workers too
                            long rather than of the ceiling being too low - so read it together with the next one.
                        </div>
                        <div className="mb-1">
                            <strong>RESOURCE_SEMAPHORE</strong> counts queries that were admitted but then queued for
                            a MEMORY GRANT. The optimizer sizes that grant from its row ESTIMATE, so statistics
                            claiming far more rows than exist make a query ask for memory the instance cannot supply,
                            and it waits however little data is really there. This is the usual root cause: it drains
                            the worker pool, and THREADPOOL is the pile-up behind it.
                        </div>
                        <div className="mb-0">
                            Both totals are <strong>cumulative since the instance started</strong>, which is why uptime
                            is shown above and each is also given per hour of uptime - the only form that is
                            comparable between environments.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderLiveManifest() {
        const { liveManifest, liveManifestLoading } = this.infrastructureStore;

        return (
            <div className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <span>
                        Live Cluster
                        <span className="badge bg-success ms-2">read from the API</span>
                    </span>
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={this.handleLoadLiveManifest}
                        disabled={liveManifestLoading}
                    >
                        {liveManifestLoading ? 'Reading…' : liveManifest ? 'Re-read cluster' : 'Read cluster'}
                    </button>
                </div>
                <div className="card-body p-0">
                    <div className="alert alert-secondary small m-2 mb-0">
                        <div className="mb-2">
                            This is the cluster <strong>as it currently is</strong>, read from the Kubernetes
                            API each time you press the button and never cached. It is{' '}
                            <strong>not the checked-in manifest</strong>: if something was committed but not
                            applied, applied but not committed, or edited by hand, what you see here is the
                            live truth and the repository is the thing that disagrees. <code>status</code>,{' '}
                            <code>managedFields</code> and the <code>last-applied-configuration</code>{' '}
                            annotation are stripped as noise.
                        </div>

                        {liveManifest && (
                            <>
                                <div className="mb-1">
                                    <strong>Covers only</strong> {liveManifest.includedKinds.join(', ')} —
                                    nothing here discovers kinds, so read silence as "not looked for", never
                                    as "not in the cluster".
                                </div>
                                <div className="mb-1">
                                    <strong>Not included:</strong>
                                </div>
                                <ul className="mb-0 ps-4">
                                    {liveManifest.excludedKinds.map(k => (
                                        <li key={k}>{k}</li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>

                    {liveManifest && (
                        <>
                            <div className="small text-muted px-3 pt-2">
                                Namespace <code>{liveManifest.namespace}</code> ·{' '}
                                {liveManifest.documents.length} objects · read{' '}
                                {new Date(liveManifest.readAtUtc).toLocaleString()}
                            </div>

                            {liveManifest.unreadableKinds.map(k => (
                                <div className="alert alert-warning small m-2 mb-0" key={k}>
                                    <strong>Could not be read:</strong> {k}
                                </div>
                            ))}

                            <div className="p-3">
                                {liveManifest.documents.map(d => (
                                    <details className="mb-2" key={`${d.kind}/${d.name}`}>
                                        <summary className="small">
                                            <span className="badge bg-secondary me-2">{d.kind}</span>
                                            <code>{d.name}</code>
                                        </summary>
                                        <pre
                                            className="bg-light border rounded p-2 small mt-2 mb-0"
                                            style={{ maxHeight: '28rem', overflow: 'auto' }}
                                        >
                                            <code>{d.yaml}</code>
                                        </pre>
                                    </details>
                                ))}
                            </div>
                        </>
                    )}

                    {!liveManifest && !liveManifestLoading && (
                        <div className="small text-muted p-3">
                            Not read yet — press <em>Read cluster</em>. Nothing is shown until then, so what
                            you see is never a stale snapshot from an earlier visit.
                        </div>
                    )}
                </div>
            </div>
        );
    }

    /**
     * Flags silohost crash dumps, and is explicit that it can only see ONE node's.
     *
     * Dumps are written to node-local disk and this pod runs on a single node, so two thirds of a
     * three-node cluster are invisible here. Reading the others would need pods/exec — arbitrary command
     * execution in any pod in the namespace — or a per-node service; neither was judged worth it for a
     * flag. So the page reports what it can see, names the nodes it cannot, and gives the commands to
     * check and collect from any of them by hand.
     */
    private renderCrashDumps() {
        const dumps = this.infrastructureStore.crashDumps;

        if (!dumps) {
            return null;
        }

        const crash = dumps.files.filter(f => f.kind === 'crash');
        const commands = this.dumpCommands(dumps);

        return (
            <div className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <span>SiloHost Crash Dumps</span>
                    {dumps.directoryReadable && (
                        <span className={`badge ${crash.length > 0 ? 'bg-danger' : 'bg-secondary'}`}>
                            {crash.length > 0
                                ? `${crash.length} crash dump${crash.length === 1 ? '' : 's'} on this node`
                                : 'none on this node'}
                        </span>
                    )}
                </div>
                <div className="card-body p-0">
                    <div className="alert alert-secondary small m-2 mb-0">
                        <div className="mb-1">
                            Dumps are written to <code>{dumps.hostPath}</code> on whichever node the silo ran
                            on.{' '}
                            {dumps.nodeName
                                ? <>This page runs on <strong>{dumps.nodeName}</strong> and can therefore only
                                    report that node.</>
                                : <>This page could not determine which node it runs on, so it can report{' '}
                                    <strong>no node at all</strong> — every node below has to be checked by
                                    hand.</>}
                        </div>
                        {dumps.unreadableNodes.length > 0 && (
                            <div className="mb-1">
                                <strong>Not shown:</strong> {dumps.unreadableNodes.join(', ')}. A dump may
                                exist on any of these and this page cannot tell — check them with the
                                commands below.
                            </div>
                        )}
                        <div className="mb-0">
                            A <strong>crash dump</strong> (<code>.dmp</code>, ~3&ndash;4&nbsp;GB) is written
                            when a silo dies on SIGSEGV. An <strong>OOM kill is a SIGKILL and produces
                            none</strong>. A <strong>heap dump</strong> (<code>.gcdump</code>, small) is
                            captured when the heap crosses its configured size. Each name is reused, so a
                            newer dump <strong>overwrites the previous one</strong> — collect promptly.
                        </div>
                    </div>

                    {!dumps.directoryReadable && (
                        <div className="alert alert-warning small m-2 mb-0">
                            <strong>Cannot read this node&apos;s dump directory.</strong>{' '}
                            {dumps.unreadableReason}
                        </div>
                    )}

                    {dumps.directoryReadable && dumps.files.length === 0 && (
                        <div className="small text-muted p-3">
                            No dumps on {dumps.nodeName ?? 'this node'}. This says nothing about the other
                            nodes.
                        </div>
                    )}

                    {dumps.files.length > 0 && (
                        <div className="table-responsive">
                            <table className="table table-sm table-striped mb-0">
                                <thead>
                                    <tr>
                                        <th>File</th>
                                        <th>Kind</th>
                                        <th>Size</th>
                                        <th>Written</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dumps.files.map(f => (
                                        <tr key={f.name}>
                                            <td><code>{f.name}</code></td>
                                            <td>
                                                <span className={`badge ${f.kind === 'crash' ? 'bg-danger' : 'bg-info text-dark'}`}>
                                                    {f.kind}
                                                </span>
                                            </td>
                                            <td>{(f.sizeBytes / (1024 * 1024)).toFixed(0)} MB</td>
                                            <td>{new Date(f.modifiedUtc).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="p-3 pt-2">
                        <div className="fw-bold small mb-1">Check every node</div>
                        <pre className="bg-light border rounded p-2 small mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                            <code>{commands.check}</code>
                        </pre>

                        <div className="fw-bold small mb-1">Copy a dump off a node</div>
                        <pre className="bg-light border rounded p-2 small mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                            <code>{commands.collect}</code>
                        </pre>

                        <div className="fw-bold small mb-1">Clear dumps on a node</div>
                        <pre className="bg-light border rounded p-2 small mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                            <code>{commands.clear}</code>
                        </pre>

                        <div className="fw-bold small mb-1">
                            Analyse a dump (the tools are already in the silo image)
                        </div>
                        <pre className="bg-light border rounded p-2 small mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                            <code>{commands.analyse}</code>
                        </pre>

                        <div className="small text-muted mt-2 mb-0">
                            Dumps contain process memory — connection strings, tokens and customer telemetry.
                            Treat a copied file as sensitive and delete it when finished.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private handleLoadLiveManifest = async () => {
        await this.infrastructureStore.loadLiveManifestAsync();
    };
    /** Built against this cluster's real node names and path rather than placeholders, so they can be
     *  pasted as-is. Only the ssh user is left for the operator to fill in. */
    private dumpCommands(dumps: CrashDumps) {
        const nodes = [dumps.nodeName, ...dumps.unreadableNodes];
        const path = dumps.hostPath;
        const silo = this.infrastructureStore.pods.find(p => p.app === 'silohost');

        return {
            check: nodes
                .map(n => `kubectl debug node/${n} -it --image=busybox -- ls -lh /host${path}`)
                .join('\n'),
            collect:
                '# from the node itself, or via a debug pod as above\n'
                + `scp <user>@${dumps.nodeName}:${path}/silohost.dmp ./silohost.dmp`,
            clear:
                '# frees node disk; the next crash writes a fresh file regardless\n'
                + `ssh <user>@${dumps.nodeName} 'sudo rm -f ${path}/*.dmp ${path}/*.gcdump'`,
            analyse:
                `kubectl exec -it ${silo ? silo.name : '<silohost-pod>'} -- dotnet-dump analyze /dumps/silohost.dmp\n`
                + '#   clrstack -all     managed stacks\n'
                + '#   dumpheap -stat    what was on the heap\n'
                + '#   pe                the exception, if any',
        };
    }

    // Derive the environment prefix (e.g. "stg-") from the running pods so the displayed kubectl
    // commands (written with an "<env>-" placeholder) match this cluster instead of a hard-coded env.
    private get envPrefix(): string {
        const pod = this.infrastructureStore.pods.find(p => !!p.app && p.name.includes(p.app));
        return pod && pod.app ? pod.name.substring(0, pod.name.indexOf(pod.app)) : '<env>-';
    }

    private renderActionModal(handle: ModalHandle, details: ActionDetails) {
        const commands = details.commands.map(c => c.replace(/<env>-/g, this.envPrefix));
        const targets = this.infrastructureStore.targets;
        const machine = details.runsOn === 'sql' ? targets.sqlServer : targets.kubernetesApi;
        const identity = details.runsOn === 'sql' ? targets.sqlIdentity : targets.kubernetesIdentity;
        const mechanism = details.runsOn === 'sql' ? 'a direct SQL client connection' : 'a Kubernetes API call';
        const handledBy = details.runsOn === 'sql'
            ? 'Microsoft SQL Server on that host'
            : "the cluster's kube-apiserver (writes to etcd; the kubelet on each node then reconciles the pods)";
        const issuedBy = details.runsOn === 'sql'
            ? 'the Internal tier — this ASP.NET Core pod — via Microsoft.Data.SqlClient (ADO.NET)'
            : 'the Internal tier — this ASP.NET Core pod — via the KubernetesClient .NET library';

        return (
            <Modal
                size="lg"
                modalHandle={handle}
                titleClass={details.headerClass}
                title={<span className="fw-bold">{details.title}</span>}
                footer={
                    <>
                        <Button color="secondary" outline onClick={handle.reject}>
                            Cancel
                        </Button>
                        <Button color={details.confirmColor} onClick={handle.resolve} disabled={details.confirmDisabled}>
                            {details.confirmLabel}
                        </Button>
                    </>
                }
            >
                <div className={`alert alert-${details.confirmColor} mb-3`}>{details.summary}</div>

                <div className="mb-3">
                    <div className="fw-bold mb-1">What will run</div>
                    <div className="small text-muted mb-1">{details.commandLabel}</div>
                    <pre className="bg-light border rounded p-2 mb-0" style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                        <code>{commands.join('\n')}</code>
                    </pre>
                </div>

                <div className="mb-3">
                    <div className="fw-bold mb-1">Where it runs</div>
                    <div className="small">{details.target}</div>
                    <ul className="small mb-0 mt-2">
                        <li><strong>Commands run on:</strong> <code>{machine ?? 'resolving…'}</code></li>
                        <li><strong>Run as:</strong> <code>{identity ?? 'resolving…'}</code></li>
                        <li><strong>Handled by:</strong> {handledBy}</li>
                        <li><strong>Issued by:</strong> {issuedBy}</li>
                        <li><strong>Working directory:</strong> none — issued in-process as {mechanism}, not a shell command in a folder.</li>
                    </ul>
                </div>

                <div className={details.note ? 'mb-3' : 'mb-0'}>
                    <div className="fw-bold mb-1">Expected wait</div>
                    <div className="small">{details.wait}</div>
                </div>

                {details.note && (
                    <div className="alert alert-secondary small mb-0">{details.note}</div>
                )}
            </Modal>
        );
    }

    // Builds the confirmation for one pod from the server-resolved plan, so every number and name in the
    // dialog is the cluster's, not the page's. A blocked plan renders the same dialog with the reason and
    // a disabled confirm button — the operator gets told why rather than left with a dead link.
    private podScaleDownDetails(plan: PodScaleDownPlan): ActionDetails {
        const blocked = !plan.canScaleDown;
        const takesTierOffline = plan.canScaleDown && plan.lastReplica;

        return {
            title: 'Scale Down Pod',
            headerClass: blocked ? 'bg-secondary text-white' : 'bg-danger text-white',
            confirmLabel: 'Scale down this pod',
            confirmColor: blocked ? 'secondary' : 'danger',
            confirmDisabled: blocked,
            runsOn: 'kubernetes',
            summary: blocked ? (
                <>
                    <strong>This pod can't be scaled down.</strong> {plan.blockedReason}
                </>
            ) : (
                <>
                    <div>
                        Kubernetes has no per-pod replica count — a pod deleted on its own is just recreated
                        by its ReplicaSet. So this does two things: it marks <code>{plan.podName}</code> as the
                        pod to drop, then takes <strong>{plan.deployment}</strong> from{' '}
                        <strong>{plan.currentReplicas}</strong> to <strong>{plan.targetReplicas}</strong> replicas —
                        that way this specific pod is the one that goes, and nothing replaces it.
                    </div>
                    {takesTierOffline && (
                        <div className="mt-2">
                            <strong>This is the last replica.</strong> Scaling it down takes the whole{' '}
                            <strong>{plan.app ?? plan.deployment}</strong> tier offline, not just one pod.
                        </div>
                    )}
                </>
            ),
            target: (
                <>
                    <div>
                        The <strong>Kubernetes API server of the cluster this Internal pod runs in</strong> — the
                        environment you're viewing right now, and only that one. Nothing leaves the cluster.
                    </div>
                    <ul className="mb-0 mt-1">
                        <li><strong>Endpoint:</strong> the in-cluster API (<code>https://kubernetes.default.svc</code>), namespace <code>default</code>.</li>
                        <li><strong>Who's asking:</strong> the <code>internal</code> pod's own ServiceAccount token + RBAC — no kubeconfig and no user credentials involved.</li>
                        <li>
                            <strong>Pod:</strong> <code>{plan.podName}</code>
                            {plan.node && <> on node <code>{plan.node}</code> — that node gets the pod's resources back.</>}
                        </li>
                        <li><strong>Controlled by:</strong> {plan.ownerKind}{plan.deployment ? <> <code>{plan.deployment}</code></> : null}.</li>
                        {!blocked && (
                            <li>
                                <strong>What it does:</strong> a <code>PATCH</code> of the pod's{' '}
                                <code>pod-deletion-cost</code> annotation, then a <code>PATCH</code> of the
                                Deployment's <code>spec.replicas</code> to {plan.targetReplicas}; the kubelet on{' '}
                                {plan.node ? <code>{plan.node}</code> : 'that node'} then terminates the pod.
                            </li>
                        )}
                        <li><strong>Left intact:</strong> the Deployment, Services, secrets, PVCs and the SQL / RabbitMQ backends — only the replica count changes.</li>
                    </ul>
                </>
            ),
            wait: blocked
                ? 'Nothing will run.'
                : 'The two API calls return in ~1–2 seconds. The pod then terminates over its grace period; a silohost pod drains Orleans and can take up to ~3 minutes to stop fully.',
            commandLabel: blocked
                ? 'Nothing would run for this pod:'
                : 'Marks this pod as the one to drop, then lowers the replica count (equivalent to):',
            commands: blocked ? ['# no action available for this pod'] : plan.commands,
            note: blocked ? undefined : (
                <>
                    <div className="mb-1">
                        <strong>This pod will not come back on its own.</strong> The replica count stays lowered
                        until someone raises it — <em>Bring Everything Back Up</em> (step 3 below) restores the
                        count recorded before the first scale-down.
                    </div>
                    <div className="mb-0">
                        <code>pod-deletion-cost</code> is how Kubernetes is told which pod to prefer removing. In
                        normal operation this is exactly the pod that goes, but it is a strong preference the
                        ReplicaSet controller honours — not a hard guarantee.
                    </div>
                </>
            ),
        };
    }

    // Show the running digest (ground truth for the bytes actually executing) truncated, full on hover,
    // and flag when the app is running more than one digest — the divergence a mutable tag would hide.
    private renderImageDigest(pod: PodStatus) {
        if (!pod.imageId) {
            return '-';
        }
        return (
            <div title={pod.imageId}>
                <code className="small fw-bold">{InfrastructureDashboard.shortDigest(pod.imageId)}</code>
                {pod.imageDrift && (
                    <span
                        className="badge bg-warning text-dark ms-1"
                        title="This app is running more than one image digest right now — a rollout in progress, or a pod running stale content."
                    >
                        mixed
                    </span>
                )}
                {pod.runningImage && (
                    <div>
                        <small
                            className="text-danger"
                            title={`The requested tag was reused. This running image is also tagged "${pod.runningImage}", so it is a different build than the requested tag now names — this pod is not running the requested build. Trust this digest, not the tag.`}
                        >
                            also tagged: {pod.runningImage}
                        </small>
                    </div>
                )}
            </div>
        );
    }

    private static shortDigest(imageId: string): string {
        const match = /sha256:([0-9a-f]{12})/i.exec(imageId);
        return match ? match[1] : imageId;
    }

    private renderStatusBadge(pod: PodStatus) {
        let badgeClass = 'badge bg-secondary';
        if (pod.status === 'Running' && pod.ready) {
            badgeClass = 'badge bg-success';
        } else if (pod.status === 'Running' && !pod.ready) {
            badgeClass = 'badge bg-warning text-dark';
        } else if (pod.status === 'Pending') {
            badgeClass = 'badge bg-info text-dark';
        } else if (pod.status === 'Failed' || pod.status === 'Unknown') {
            badgeClass = 'badge bg-danger';
        }
        return <span className={badgeClass}>{pod.status}</span>;
    }

    private handleRefresh = async () => {
        await this.infrastructureStore.loadPodsAsync();
    };

    private handleScaleDown = async () => {
        if (await this.scaleDownModal.confirm()) {
            await this.infrastructureStore.scaleDownAllAsync();
        }
    };

    private handleClearOrleans = async () => {
        if (await this.clearOrleansModal.confirm()) {
            await this.infrastructureStore.clearOrleansMembershipAsync();
        }
    };

    private handleScaleUp = async () => {
        if (await this.scaleUpModal.confirm()) {
            await this.infrastructureStore.scaleUpAllAsync();
        }
    };

    // Resolve the plan server-side FIRST so the confirmation can name the owning deployment and the real
    // replica arithmetic instead of a guess. If it can't be resolved there's nothing to confirm — the
    // reason is shown on the page.
    private handlePodScaleDown = async (podName: string) => {
        if (!(await this.infrastructureStore.loadPodScaleDownPlanAsync(podName))) {
            return;
        }

        if (await this.podScaleDownModal.confirm()) {
            await this.infrastructureStore.scaleDownPodAsync(podName);
        }
    };

    @inject
    private infrastructureStore!: InfrastructureStore;
}
